import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';

const SimpleMenu = React.memo(() => {
  // --- State for Search ---
  const [searchQuery, setSearchQuery] = useState('');
  // --- State for Scroll to Top ---
  const [showBackToTop, setShowBackToTop] = useState(false);

  // --- Fetch menu data using React Query ---
  const { data: menuData, isLoading, error } = useQuery({
    queryKey: ['menuData'], // Use the same query key as Menu.jsx if you want shared cache
    queryFn: async () => {
      const response = await fetch("https://menufetch.mcottagega.workers.dev/"); // Replace with your actual API endpoint
      if (!response.ok) throw new Error(`API Error fetching menu: ${response.statusText}`);
      return response.json();
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    cacheTime: 1000 * 60 * 30, // Keep data for 30 minutes
    refetchOnWindowFocus: false,
  });

  // Get restaurant info from menuData or use defaults
  const restaurantName = menuData?.restaurant?.name || "Masala Cottage";
  const restaurantInfo = menuData?.restaurant?.description || "True Indian Cuisine";
  
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // --- Filter Logic ---
  const filteredResults = useMemo(() => {
    if (!menuData || !searchQuery.trim()) {
      return null; // Return null when not searching to indicate showing categories
    }

    const query = searchQuery.toLowerCase().trim();
    const results = [];
    menuData.categories?.forEach(category => {
      if (category.active) {
        category.items?.forEach(item => {
          if (item.active) {
            const name = item.name.toLowerCase();
            const description = (item.description || '').toLowerCase();
            if (name.includes(query) || description.includes(query)) {
              results.push(item); // Add matching items to a flat list
            }
          }
        });
      }
    });
    return results;
  }, [menuData, searchQuery]);

  // --- Active Categories (used when not searching) ---
  const activeCategories = useMemo(() =>
    menuData?.categories?.filter(category => category.active) || [],
    [menuData]
  );

  // --- Back to top visibility handler ---
  useEffect(() => {
    const handleScroll = () => {
      // Show button when scrolled down more than 300px
      setShowBackToTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    // Cleanup listener on component unmount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Empty dependency array ensures this runs only once on mount/unmount

  // --- Handler for back to top button ---
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // Smooth scrolling animation
    });
  };

  // --- Helper to render a single item (used in both views) ---
  const renderMenuItem = (item) => {
    const itemSizes = item.sizes;
    const hasSizes = Array.isArray(itemSizes) && itemSizes.length > 0;
    const baseItemPrice = Number(item.price) || 0;

    return (
      <article key={item.id} className="item py-3 border-b border-gray-100 last:border-b-0" itemScope itemType="https://schema.org/MenuItem">
        {hasSizes ? (
          // --- Item WITH Sizes ---
          <div>
            <h3 className="text-lg font-medium text-gray-800" itemProp="name">
              {item.name}
            </h3>
            {item.description && (
              <p className="text-gray-600 text-sm mt-1 item-description" itemProp="description">
                {item.description}
              </p>
            )}
            <ul className="list-none pl-4 mt-1 size-list">
              {itemSizes.map((sizeOption, idx) => {
                const optionPriceAddition = Number(sizeOption.price) || 0;
                const finalOptionPrice = optionPriceAddition === 0 ? baseItemPrice : baseItemPrice + optionPriceAddition;
                return (
                  <li key={idx} className="flex justify-between items-baseline gap-2 size-item">
                    <span>{sizeOption.name || 'Size'}</span>
                    <div className="border-b border-dotted border-gray-300 flex-grow mx-2"></div>
                    <span className="font-medium text-primary whitespace-nowrap">
                      ${finalOptionPrice.toFixed(2)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          // --- Item WITHOUT Sizes ---
          <div>
            <div className="flex justify-between items-baseline gap-4">
              <h3 className="text-lg font-medium text-gray-800" itemProp="name">
                {item.name}
              </h3>
              <div className="border-b border-dotted border-gray-300 flex-grow"></div>
              <span className="font-medium text-primary whitespace-nowrap" itemProp="offers" itemScope itemType="https://schema.org/Offer">
                <meta itemProp="priceCurrency" content="USD" />
                <span itemProp="price">${baseItemPrice.toFixed(2)}</span>
              </span>
            </div>
            {item.description && (
              <p className="text-gray-600 text-sm mt-1 item-description" itemProp="description">
                {item.description}
              </p>
            )}
          </div>
        )}
      </article>
    );
  };
  // --- End renderMenuItem helper ---

  // --- Loading State ---
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // --- Error State ---
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 max-w-md" role="alert">
          <p className="font-bold">Error Loading Menu</p>
          <p>{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Menu - {restaurantName}</title>
        {/* Add other relevant meta tags if needed */}
      </Helmet>

      <article
        className="simple-menu-view" // Use a different class name if needed
        itemScope
        itemType="https://schema.org/Menu"
      >
        {/* Keep print styles if you want printing from this view */}
        <style>
          {`
            @media print {
              .no-print { display: none !important; }
              @page { 
                margin: 1.5cm;
                size: portrait;
              }
              .print-view { 
                padding: 0;
                max-width: none;
              }
              .page-break { 
                page-break-before: always;
              }
              .category { 
                page-break-inside: avoid;
              }
              .item {
                page-break-inside: avoid;
              }
              /* Reduce space slightly for print */
              .menu-content { space-y-8 !important; }
              .item-description { font-size: 0.9em; }
              .size-list { margin-top: 0.25rem; }
              .size-item { font-size: 0.9em; }
            }
            .print-view {
              max-width: 1000px;
              margin: 0 auto;
              padding: 2rem;
              font-family: 'Georgia', serif;
              background: white;
            }
          `}
        </style>

        {/* Search Bar */}
        <div className="mb-8 sticky top-0 bg-white p-4 shadow-md z-10">
            <div className="relative flex-grow max-w-lg mx-auto"> {/* Centered search */}
                <input
                    type="text"
                    placeholder="Search menu items..."
                    className="w-full px-4 py-2 bg-gray-100 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <i className="fas fa-search text-gray-400"></i> {/* Ensure FontAwesome is loaded */}
                </div>
            </div>
        </div>

        {/* Container for the menu content */}
        <div className="px-6 sm:px-10 lg:px-16"> {/* Kept padding for screen edges */}

            {/* Header */}
            <header className="text-center mb-12 border-b-2 border-gray-200 pb-8">
                <h1 className="text-4xl font-bold text-primary mb-4" itemProp="name">
                    {restaurantName}
                </h1>
                {restaurantInfo && (
                    <p className="text-gray-600 mb-4">{restaurantInfo}</p>
                )}
                <p className="text-sm text-gray-500">Menu as of {today}</p>
            </header>

            {/* Menu Content: Either search results or categories */}
            <div className="menu-content space-y-12" role="list">
                {filteredResults !== null ? (
                    // --- Render Search Results ---
                    <section className="category"> {/* Use category class for spacing */}
                        <h2 className="text-2xl font-bold text-primary border-b border-secondary pb-2 mb-6">
                            Search Results for "{searchQuery}"
                        </h2>
                        {filteredResults.length > 0 ? (
                             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-x-8 gap-y-4"> {/* Maybe add more columns for wider screens */}
                                {filteredResults.map(item => renderMenuItem(item))}
                            </div>
                        ) : (
                            <p className="text-gray-600 italic">No items found matching your search.</p>
                        )}
                    </section>
                ) : (
                    // --- Render Categories ---
                    activeCategories.map((category, index) => (
                        <section
                            key={category.id}
                            className={`category ${index > 0 && index % 3 === 0 ? 'page-break' : ''}`} // Keep page break logic for potential printing
                            itemProp="hasMenuSection" itemScope itemType="https://schema.org/MenuSection"
                        >
                            <h2 className="text-2xl font-bold text-primary border-b border-secondary pb-2 mb-6" itemProp="name">
                                {category.name}
                            </h2>
                            {category.description && (
                                <p className="text-gray-600 mb-8 italic" itemProp="description">
                                    {category.description}
                                </p>
                            )}
                             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-x-8 gap-y-4"> {/* Maybe add more columns for wider screens */}
                                {category.items
                                    .filter(item => item.active)
                                    .map(item => renderMenuItem(item)) // Use the helper
                                }
                            </div>
                        </section>
                    ))
                )}
            </div>

            {/* Footer */}
            <footer className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
                <p>Prices and menu items subject to change without notice.</p>
                <p className="mt-2">For the most up-to-date menu, please visit our website or contact us directly.</p>
            </footer>
        </div> {/* End content container */}

        {/* --- Back to top button --- */}
        <button
          className={`no-print fixed bottom-8 right-8 bg-secondary text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-opacity duration-300 hover:bg-secondary/90 ${
            showBackToTop ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={scrollToTop}
          aria-label="Scroll back to top"
        >
          {/* Replace Font Awesome icon with SVG */}
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-6 w-6" // Use Tailwind classes for size
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor" 
            strokeWidth={2} // Adjust stroke width as needed
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </button>
        {/* --- End Back to top button --- */}

      </article>
    </>
  );
});

SimpleMenu.displayName = 'SimpleMenu'; // Set display name

export default SimpleMenu; 